import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { PullToRefreshService } from './pull-to-refresh.service';
import { isFrameScrollEvent, isShellLayout, pageScrollTop } from '../utils/page-scroll';

/**
 * The pull-down gesture and its little indicator. Mounted ONCE in the app shell (app.component.html); pages
 * register what "refresh" means through PullToRefreshService.attach(). Touch only (phones and tablets).
 *
 * Starts only when: this screen can refresh (the page has its own refresh, or it may simply be rebuilt), the page is scrolled to the very top, one finger, and the
 * finger did not land on something that scrolls or drags by itself (tally grid, text fields, dialogs and
 * side panels, a scrolled inner list). Sideways movement cancels it. Nothing here touches the grid's own
 * scrolling and no animation runs inside any grid.
 */
@Component({
  selector: 'app-pull-to-refresh',
  standalone: true,
  template: `
    <div #indicator class="ptr" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
        <g #arrow class="ptr-arrow"><path d="M12 5v11m0 0-4-4m4 4 4-4" /></g>
        <circle class="ptr-spin" cx="12" cy="12" r="8" />
      </svg>
    </div>
  `,
  styles: [`
    .ptr {
      position: fixed; left: 50%; top: 0; z-index: 1100; width: 38px; height: 38px; margin-left: -19px;
      display: grid; place-items: center; border-radius: 50%; pointer-events: none;
      color: var(--primary-color, #1684ac); background: var(--surface-card, #fff);
      border: 1px solid var(--surface-border, rgba(0, 0, 0, 0.1));
      box-shadow: 0 6px 18px rgba(20, 23, 58, 0.22);
      opacity: 0; transform: translate3d(0, -60px, 0); will-change: transform, opacity;
    }
    .ptr.settle { transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease; }
    .ptr-arrow { transform-origin: 12px 12px; transition: transform 0.15s ease; }
    .ptr.ready .ptr-arrow { transform: rotate(180deg); }
    .ptr-spin { display: none; stroke-dasharray: 38 14; transform-origin: 12px 12px; }
    .ptr.loading .ptr-arrow { display: none; }
    .ptr.loading .ptr-spin { display: block; animation: ptrSpin 0.8s linear infinite; }
    @keyframes ptrSpin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) {
      .ptr.settle, .ptr-arrow { transition: none; }
      .ptr.loading .ptr-spin { animation-duration: 1.6s; }
    }
  `],
})
export class PullToRefreshComponent implements OnInit, OnDestroy {
  private readonly zone = inject(NgZone);
  private readonly svc = inject(PullToRefreshService);

  @ViewChild('indicator', { static: true }) indicator!: ElementRef<HTMLElement>;

  /** how far the indicator may travel, and how far it must be pulled to count */
  private readonly MAX = 96;
  private readonly TRIGGER = 62;
  private readonly DAMPING = 0.55;

  private tracking = false;
  private pulling = false;
  private busy = false;
  private startX = 0;
  private startY = 0;
  private pull = 0;
  private baseTop = 0;

  private readonly onStart = (e: TouchEvent) => this.touchStart(e);
  private readonly onMove = (e: TouchEvent) => this.touchMove(e);
  private readonly onEnd = () => this.touchEnd();
  private readonly onScroll = () => this.syncMoveListener();
  /** App frame (html.ll-shell): the page scrolls inside app-root > .container, and scroll events of an inner box do not reach the window
   *  unless listened for in the capture phase. Cheap: it returns at once unless the frame is on and the scroller is the one scrolling. */
  private readonly onFrameScroll = (e: Event) => { if (isShellLayout() && isFrameScrollEvent(e)) this.syncMoveListener(); };
  private moveOn = false;

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      document.addEventListener('touchstart', this.onStart, { passive: true });
      document.addEventListener('touchend', this.onEnd, { passive: true });
      document.addEventListener('touchcancel', this.onEnd, { passive: true });
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('scroll', this.onFrameScroll, { passive: true, capture: true });
      this.syncMoveListener();
    });
  }

  /** 2026-10-02: the touchmove listener is NOT passive (once a pull has started we stop the browser's own bounce / reload), and a
   *  non-passive touchmove listener on the document makes iPhone Safari wait for our code on every scroll frame: the fixed footer and the
   *  round menu button lagged and floated over the page. So it exists ONLY while the page is at the very top (the only place a pull can
   *  start) and is removed as soon as the page scrolls down. Do not register it permanently again. */
  private syncMoveListener(): void {
    const atTop = this.pageTop() <= 0;
    if (atTop && !this.moveOn) {
      document.addEventListener('touchmove', this.onMove, { passive: false });
      this.moveOn = true;
    } else if (!atTop && this.moveOn && !this.pulling) {
      document.removeEventListener('touchmove', this.onMove);
      this.moveOn = false;
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('touchstart', this.onStart);
    document.removeEventListener('touchmove', this.onMove);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('scroll', this.onFrameScroll, { capture: true });
    document.removeEventListener('touchend', this.onEnd);
    document.removeEventListener('touchcancel', this.onEnd);
  }

  private pageTop(): number {
    return pageScrollTop();
  }

  /** True when this touch must NOT start a pull. */
  private blocked(target: EventTarget | null): boolean {
    if (this.busy || !this.svc.enabled) return true;
    const body = document.body.classList;
    if (body.contains('p-overflow-hidden') || body.contains('su-sheet-open')) return true; // a dialog / sheet is open
    if (this.pageTop() > 0) return true;
    const el = target instanceof HTMLElement ? target : (target as Node | null)?.parentElement ?? null;
    if (!el) return false;
    if (el.closest('input, textarea, select, [contenteditable="true"], [data-no-ptr], .ag-root-wrapper, .p-dialog, .p-drawer, .p-popover, .p-overlay, .p-select-overlay, .p-datepicker, .p-autocomplete-overlay, .p-multiselect-overlay, .p-menu-overlay')) return true;
    // inside a list that is itself scrolled down: the finger belongs to that list
    for (let n: HTMLElement | null = el; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
      const oy = getComputedStyle(n).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1 && n.scrollTop > 0) return true;
    }
    return false;
  }

  private touchStart(e: TouchEvent): void {
    this.tracking = false;
    this.pulling = false;
    if (e.touches.length !== 1 || this.blocked(e.target)) return;
    this.tracking = true;
    this.startX = e.touches[0].clientX;
    this.startY = e.touches[0].clientY;
    this.pull = 0;
    // sits just under the app header (it is sticky at the top)
    const header = document.querySelector('header.header') as HTMLElement | null;
    this.baseTop = (header?.offsetHeight ?? 0) + 6;
  }

  private touchMove(e: TouchEvent): void {
    if (!this.tracking) return;
    if (e.touches.length !== 1) { this.reset(); return; }
    const t = e.touches[0];
    const dy = t.clientY - this.startY;
    const dx = t.clientX - this.startX;
    if (!this.pulling) {
      // sideways, or upwards, or the page moved: not a pull
      if (Math.abs(dx) > Math.abs(dy) + 4 || dy < -6 || this.pageTop() > 0) { this.tracking = false; return; }
      if (dy < 10) return;
      this.pulling = true;
      const el = this.indicator.nativeElement;
      el.classList.remove('settle', 'loading', 'ready');
    }
    if (e.cancelable) e.preventDefault();
    this.pull = Math.min(this.MAX, Math.max(0, dy * this.DAMPING));
    this.render(this.pull);
  }

  private render(pull: number): void {
    const el = this.indicator.nativeElement;
    el.style.opacity = String(Math.min(1, pull / 40));
    el.style.transform = `translate3d(0, ${this.baseTop + pull - 46}px, 0)`;
    el.classList.toggle('ready', pull >= this.TRIGGER);
  }

  private touchEnd(): void {
    if (!this.tracking || !this.pulling) { this.tracking = false; return; }
    const trigger = this.pull >= this.TRIGGER && navigator.onLine !== false;
    this.tracking = false;
    this.pulling = false;
    if (!trigger) { this.hide(); return; }

    this.busy = true;
    const el = this.indicator.nativeElement;
    el.classList.add('settle', 'loading');
    el.classList.remove('ready');
    el.style.transform = `translate3d(0, ${this.baseTop + 16}px, 0)`;
    el.style.opacity = '1';
    this.zone.run(() => {
      this.svc.run().finally(() => {
        this.busy = false;
        this.hide();
      });
    });
  }

  private reset(): void {
    this.tracking = false;
    this.pulling = false;
    this.hide();
  }

  private hide(): void {
    const el = this.indicator.nativeElement;
    el.classList.add('settle');
    el.classList.remove('ready', 'loading');
    el.style.opacity = '0';
    el.style.transform = `translate3d(0, ${this.baseTop - 60}px, 0)`;
  }
}
